/**
 * JWT exchange helpers for createAuth
 * Extracted to reduce function size
 */

import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import type { IBaseApiService } from "@Front/Api/BaseApiServiceFactory";
import { buildBackendUrl } from "./authUrlHelpers";
import { FrontendVariables } from "@Front/Config/VariablesFront";

/**
 * Check if current environment is test, demo or pprod
 */
export function isTestDemoOrPprod(): boolean {
	const keywords = ["test", "demo", "pprod", "preprod", "staging"];
	const check = (str: string): boolean => keywords.some((k) => str.includes(k));

	let hostToCheck = "";
	if (typeof window === "undefined") {
		const frontAppHost = (process.env["NEXT_PUBLIC_FRONT_APP_HOST"] || "").toLowerCase();
		const appEnvName = (process.env["NEXTJS_APP_ENV_NAME"] || "").toLowerCase();
		hostToCheck = frontAppHost + appEnvName;
	} else {
		const variables = FrontendVariables.getInstance();
		const host = window.location.host.toLowerCase();
		const frontAppHost = (variables.FRONT_APP_HOST || "").toLowerCase();
		hostToCheck = host + frontAppHost;
	}

	return check(hostToCheck);
}

function buildBaseJwtLogData(response: { accessToken?: string; refreshToken?: string }): Record<string, unknown> {
	const accessToken = response?.accessToken;
	const refreshToken = response?.refreshToken;
	return {
		hasAccessToken: Boolean(accessToken),
		hasRefreshToken: Boolean(refreshToken),
		accessTokenLength: accessToken?.length ?? 0,
		refreshTokenLength: refreshToken?.length ?? 0,
	};
}

function addTokensIfTestEnv(base: Record<string, unknown>, response: { accessToken: string; refreshToken: string }, isTestEnv: boolean): Record<string, unknown> {
	if (!isTestEnv || !response?.accessToken || !response?.refreshToken) return base;
	return { ...base, accessToken: response.accessToken, refreshToken: response.refreshToken };
}

function buildJwtLogData(response: { accessToken: string; refreshToken: string }, isTestEnv: boolean): Record<string, unknown> {
	const base = buildBaseJwtLogData(response);
	return addTokensIfTestEnv(base, response, isTestEnv);
}
function logJwtExchangeSuccess(response: { accessToken: string; refreshToken: string }, isTestEnv: boolean): void {
	LoggerService.info("✅ [IdNot] JWT tokens received from API", buildJwtLogData(response, isTestEnv));
}

function logJwtExchangeError(err: unknown, autorizationCode: string | string[], url: string): void {
	LoggerService.error("❌ [IdNot] Failed to exchange authorization code for JWT tokens", {
		authorizationCode: typeof autorizationCode === "string" ? autorizationCode.substring(0, 10) + "..." : "array",
		url,
		error: err,
	});
}

function resolveIdNotAuthorizationCode(autorizationCode: string | string[]): string | null {
	if (Array.isArray(autorizationCode)) {
		const first = autorizationCode[0];
		return typeof first === "string" && first.trim().length > 0 ? first.trim() : null;
	}
	if (typeof autorizationCode === "string" && autorizationCode.trim().length > 0) {
		return autorizationCode.trim();
	}
	return null;
}

/**
 * Exchanges authorization code for JWT tokens
 */
export async function getIdnotJwt(
	baseApi: IBaseApiService,
	autorizationCode: string | string[],
	redirectUri?: string,
): Promise<{ accessToken: string; refreshToken: string }> {
	const code = resolveIdNotAuthorizationCode(autorizationCode);
	const url = buildBackendUrl(baseApi, "idnot/user/oauth-exchange");
	const isTestEnv = isTestDemoOrPprod();

	try {
		if (!code) {
			const err = new Error("Missing IdNot authorization code");
			logJwtExchangeError(err, autorizationCode, url.toString());
			baseApi.onError(err);
			return Promise.reject(err);
		}

		LoggerService.info("🔄 [IdNot] Exchanging authorization code for JWT tokens", {
			authorizationCode: code.substring(0, 10) + "...",
			url: url.toString(),
			hasRedirectUri: Boolean(redirectUri?.trim()),
		});

		const payload: { code: string; redirect_uri?: string } = { code };
		if (redirectUri?.trim()) {
			payload.redirect_uri = redirectUri.trim();
		}
		const response = await baseApi.postRequest<{ accessToken: string; refreshToken: string }>(url, payload);
		logJwtExchangeSuccess(response, isTestEnv);
		return response;
	} catch (err) {
		logJwtExchangeError(err, autorizationCode, url.toString());
		baseApi.onError(err);
		return Promise.reject(err);
	}
}
